import { useEffect, useRef, useState } from "react";

/**
 * @param {IntersectionObserverInit} options
 * @param {{ once?: boolean, resetOnExit?: boolean }} config
 */
export function useInView(options, config = {}) {
  const { once = false, resetOnExit = true } = config;
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        if (once) io.unobserve(el);
      } else if (resetOnExit) {
        setInView(false);
      }
    }, options);

    io.observe(el);
    return () => io.disconnect();
  }, [options, once, resetOnExit]);

  return { ref, inView };
}

/**
 * useInViewHysteresis
 *
 * @param {IntersectionObserverInit} options - IO options (root, rootMargin, threshold).
 * @param {{ enter?: number, exit?: number, once?: boolean }} config
 *  - enter: ratio to mark inView=true (default 0.6)
 *  - exit: ratio to mark inView=false (default 0)  // use 0 to mean "fully gone"
 *  - once: if true, unobserves after the first enter
 */
export function useInViewHysteresis(options, config = {}) {
  const { enter = 0.6, exit = 0, once = false } = config;

  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const inViewRef = useRef(false);

  const setInViewSync = (v) => {
    inViewRef.current = v;
    setInView(v);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clamp01 = (v) => Math.min(1, Math.max(0, v));

    const base = options?.threshold;
    const list = Array.isArray(base) ? base : base != null ? [base] : [];
    const thresholds = Array.from(
      new Set([...list, clamp01(enter), clamp01(exit), 0, 1])
    ).sort((a, b) => a - b);

    const EPS = 1e-6;

    const io = new IntersectionObserver(([entry]) => {
      const r = entry.intersectionRatio;
      const hitEnter = r + EPS >= enter;                      
      const hitExit = !entry.isIntersecting || r <= exit + EPS;

      if (!inViewRef.current && hitEnter) {
        setInViewSync(true);
        if (once) io.unobserve(el);
      } else if (inViewRef.current && hitExit && !once) {
        setInViewSync(false);
      }
    }, { ...options, threshold: thresholds });

    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.root, options?.rootMargin, enter, exit, once]);

  return { ref, inView };
}